import { t as _objectSpread2 } from "./objectSpread2-CtW7yo8J.js";
import { C as noop, E as identity, S as scrollTo, T as isModifierEvent, _ as Hit, a as NoResultsScreen, b as SearchBoxForm, d as useRefreshOnInitialQuery, f as useModalRefs, g as useDocSearchFacets, h as useSendItemClickEvent, i as ResultsScreen, l as useStoredDocSearches, m as useInitialModalQuery, n as buildNoQuerySources, o as ErrorScreen, p as useModalEnvironment, r as buildQuerySources, s as StoredSearchesSections, t as normalizeDocSearchIndexes, u as useSaveRecentSearch, v as Footer, w as isQueryEmpty, x as FacetBar, y as ModalShell } from "./normalizeDocSearchIndexes-COOsM0PD.js";
import { t as _objectWithoutProperties } from "./objectWithoutProperties-mN58NJZT.js";
import { t as useSearchClient } from "./useSearchClient-DUbFU6oe.js";
import React from "react";
import { createAutocomplete } from "@algolia/autocomplete-core";

//#region src/components/KeywordSearchBox.tsx
const _excluded$3 = ["translations"];
function KeywordSearchBox(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$3);
	const { clearButtonTitle = "Clear", clearButtonAriaLabel = "Clear the query", closeButtonText = "Close", closeButtonAriaLabel = "Close", searchInputLabel = "Search" } = translations;
	const actionsBeforeClose = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("button", {
		className: "DocSearch-Clear",
		type: "reset",
		"aria-label": clearButtonAriaLabel,
		hidden: !props.state.query,
		tabIndex: props.state.query ? 0 : -1,
		"aria-hidden": !props.state.query ? "true" : "false"
	}, clearButtonTitle), props.state.query && /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Divider" }));
	return /* @__PURE__ */ React.createElement(SearchBoxForm, _objectSpread2(_objectSpread2({}, props), {}, {
		clearButtonTitle,
		clearButtonAriaLabel,
		closeButtonText,
		closeButtonAriaLabel,
		searchInputLabel,
		inputProps: { enterKeyHint: "search" },
		actionsBeforeClose
	}));
}

//#endregion
//#region src/components/KeywordStartScreen.tsx
const _excluded$2 = ["translations"];
function KeywordStartScreen(_ref) {
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$2);
	return /* @__PURE__ */ React.createElement("div", { className: "DocSearch-Dropdown-Container" }, /* @__PURE__ */ React.createElement(StoredSearchesSections, _objectSpread2(_objectSpread2({}, props), {}, { translations })));
}

//#endregion
//#region src/ScreenState.tsx
const _excluded$1 = ["translations"];
const ScreenState = React.memo((_ref) => {
	var _props$state;
	let { translations = {} } = _ref, props = _objectWithoutProperties(_ref, _excluded$1);
	if (((_props$state = props.state) === null || _props$state === void 0 ? void 0 : _props$state.status) === "error") return /* @__PURE__ */ React.createElement(ErrorScreen, { translations: translations === null || translations === void 0 ? void 0 : translations.errorScreen });
	if (isQueryEmpty(props.state.query)) return /* @__PURE__ */ React.createElement(KeywordStartScreen, _objectSpread2(_objectSpread2({}, props), {}, {
		hasCollections: props.hasCollections,
		translations: translations === null || translations === void 0 ? void 0 : translations.startScreen
	}));
	if (!props.hasCollections) return /* @__PURE__ */ React.createElement(NoResultsScreen, _objectSpread2(_objectSpread2({}, props), {}, { translations: translations === null || translations === void 0 ? void 0 : translations.noResultsScreen }));
	return /* @__PURE__ */ React.createElement(ResultsScreen, _objectSpread2(_objectSpread2({}, props), {}, { translations: translations === null || translations === void 0 ? void 0 : translations.resultsScreen }));
}, function areEqual(prevProps, nextProps) {
	const isLoading = nextProps.state.status === "loading" || nextProps.state.status === "stalled";
	const isWaitingForCollections = nextProps.state.status === "idle" && !isQueryEmpty(nextProps.state.query) && prevProps.state.query !== nextProps.state.query && prevProps.state.collections === nextProps.state.collections;
	return isLoading || isWaitingForCollections;
});

//#endregion
//#region src/DocSearchModal.tsx
const _excluded = [
	"appId",
	"apiKey",
	"maxResultsPerGroup",
	"theme",
	"onClose",
	"transformItems",
	"hitComponent",
	"resultsFooterComponent",
	"navigator",
	"initialScrollY",
	"transformSearchClient",
	"disableUserPersonalization",
	"initialQuery",
	"translations",
	"getMissingResultsUrl",
	"insights",
	"recentSearchesLimit",
	"recentSearchesWithFavoritesLimit",
	"indices",
	"facets",
	"footerAction"
];
const _excluded2 = [
	"footer",
	"searchBox",
	"facets"
];
function DocSearchModal(_ref) {
	var _translations$searchB;
	let { appId, apiKey, maxResultsPerGroup, theme, onClose = noop, transformItems = identity, hitComponent = Hit, resultsFooterComponent = () => null, navigator, initialScrollY = 0, transformSearchClient = identity, disableUserPersonalization = false, initialQuery: initialQueryFromProp = "", translations = {}, getMissingResultsUrl, insights = false, recentSearchesLimit = 7, recentSearchesWithFavoritesLimit = 4, indices, facets, footerAction } = _ref, props = _objectWithoutProperties(_ref, _excluded);
	const { footer: footerTranslations, searchBox: searchBoxTranslations, facets: facetBarTranslations } = translations, screenStateTranslations = _objectWithoutProperties(translations, _excluded2);
	const [state, setState] = React.useState({
		query: "",
		collections: [],
		completion: null,
		context: {},
		isOpen: false,
		activeItemId: null,
		status: "idle"
	});
	const placeholder = (translations === null || translations === void 0 || (_translations$searchB = translations.searchBox) === null || _translations$searchB === void 0 ? void 0 : _translations$searchB.placeholderText) || props.placeholder || "Search docs";
	const { containerRef, modalRef, formElementRef, dropdownRef, inputRef } = useModalRefs();
	const { initialQuery, initialQueryFromSelection } = useInitialModalQuery(initialQueryFromProp);
	const searchClient = useSearchClient(appId, apiKey, transformSearchClient);
	const indexes = React.useMemo(() => normalizeDocSearchIndexes({ indices }), [indices]);
	const defaultIndexName = indexes[0].name;
	const autocompleteRef = React.useRef(void 0);
	const { visibleFacets, facetSelections, facetSelectionsRef, handleFacetSelectionChange, clearFacetSelections } = useDocSearchFacets({
		facets,
		indexes,
		searchClient,
		onSelectionsChange: () => {
			var _autocompleteRef$curr;
			return (_autocompleteRef$curr = autocompleteRef.current) === null || _autocompleteRef$curr === void 0 ? void 0 : _autocompleteRef$curr.refresh();
		}
	});
	const { favoriteSearches, recentSearches } = useStoredDocSearches({
		defaultIndexName,
		recentSearchesLimit,
		recentSearchesWithFavoritesLimit
	});
	const saveRecentSearch = useSaveRecentSearch({
		favoriteSearches,
		recentSearches,
		disableUserPersonalization
	});
	const sendItemClickEvent = useSendItemClickEvent(state);
	if (!autocompleteRef.current) autocompleteRef.current = createAutocomplete({
		id: "docsearch",
		defaultActiveItemId: 0,
		openOnFocus: true,
		initialState: {
			query: initialQuery,
			context: { searchSuggestions: [] }
		},
		insights: Boolean(insights),
		navigator,
		onStateChange(changes) {
			setState(changes.state);
		},
		getSources({ query, state: sourcesState, setContext, setStatus }) {
			if (isQueryEmpty(query)) return buildNoQuerySources({
				recentSearches,
				favoriteSearches,
				saveRecentSearch,
				onClose,
				disableUserPersonalization
			});
			const querySourcesState = { context: sourcesState.context };
			return buildQuerySources({
				query,
				state: querySourcesState,
				setContext,
				setStatus,
				searchClient,
				indexes,
				insights: Boolean(insights),
				appId,
				apiKey,
				maxResultsPerGroup,
				transformItems,
				saveRecentSearch,
				onClose,
				facetSelections: facetSelectionsRef
			});
		}
	});
	const autocomplete = autocompleteRef.current;
	const { getEnvironmentProps, getRootProps, refresh } = autocomplete;
	useModalEnvironment({
		getEnvironmentProps,
		containerRef,
		dropdownRef,
		formElementRef,
		inputRef,
		initialScrollY,
		modalRef,
		theme
	});
	React.useEffect(() => {
		if (dropdownRef.current) scrollTo(dropdownRef.current);
	}, [state.query, dropdownRef]);
	useRefreshOnInitialQuery({
		initialQuery,
		inputRef,
		refresh
	});
	let showDocsearchDropdown = true;
	const hasCollections = state.collections.some((collection) => collection.items.length > 0);
	if (state.status === "idle" && hasCollections === false && isQueryEmpty(state.query)) showDocsearchDropdown = false;
	return /* @__PURE__ */ React.createElement(ModalShell, {
		state,
		containerRef,
		modalRef,
		formElementRef,
		dropdownRef,
		getRootProps,
		showDropdown: showDocsearchDropdown,
		searchBox: /* @__PURE__ */ React.createElement(KeywordSearchBox, _objectSpread2(_objectSpread2({}, autocomplete), {}, {
			state,
			placeholder: placeholder || "Search docs",
			autoFocus: isQueryEmpty(initialQuery),
			inputRef,
			isFromSelection: Boolean(initialQuery) && initialQuery === initialQueryFromSelection,
			translations: searchBoxTranslations,
			onClose
		})),
		filterBar: !isQueryEmpty(state.query) ? /* @__PURE__ */ React.createElement(FacetBar, {
			facets: visibleFacets,
			selections: facetSelections,
			translations: facetBarTranslations,
			clearSelections: clearFacetSelections,
			onSelectionChange: handleFacetSelectionChange
		}) : null,
		screenState: /* @__PURE__ */ React.createElement(ScreenState, _objectSpread2(_objectSpread2({}, autocomplete), {}, {
			indexName: defaultIndexName,
			state,
			hitComponent,
			resultsFooterComponent,
			disableUserPersonalization,
			recentSearches,
			favoriteSearches,
			inputRef,
			translations: screenStateTranslations,
			getMissingResultsUrl,
			hasCollections,
			resultBadgeKey: props.resultBadgeKey,
			onItemClick: (item, event) => {
				sendItemClickEvent(item);
				saveRecentSearch(item);
				if (!isModifierEvent(event)) onClose();
			}
		})),
		footer: /* @__PURE__ */ React.createElement(Footer, {
			translations: footerTranslations,
			footerAction
		}),
		onClose
	});
}

//#endregion
export { DocSearchModal as t };
//# sourceMappingURL=DocSearchModal-CKubRnjs.js.map